import { Auth } from '@easylife365/react/auth';
import { Client } from '@microsoft/microsoft-graph-client';
import { HttpError } from '@easylife365/react';

export type EasyLifeRequestInit<TRequest> = {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  request?: TRequest;
  signal?: AbortSignal;
};

export function httpGet(signal?: AbortSignal): EasyLifeRequestInit<undefined> {
  return {
    method: 'GET',
    signal,
  };
}

export function httpPost<TRequest = undefined>(
  request?: TRequest,
  signal?: AbortSignal
): EasyLifeRequestInit<TRequest> {
  return {
    method: 'POST',
    request,
    signal,
  };
}

export function httpPut<TRequest = undefined>(request?: TRequest, signal?: AbortSignal): EasyLifeRequestInit<TRequest> {
  return {
    method: 'PUT',
    request,
    signal,
  };
}

export function httpPatch<TRequest = undefined>(
  request?: TRequest,
  signal?: AbortSignal
): EasyLifeRequestInit<TRequest> {
  return {
    method: 'PATCH',
    request,
    signal,
  };
}

export function httpDelete(signal?: AbortSignal): EasyLifeRequestInit<undefined> {
  return {
    method: 'DELETE',
    signal,
  };
}

export type StreamerCallback<TResponse> = (next: TResponse) => Promise<void> | void;

export async function toJson<TResponse>(response: Response): Promise<TResponse> {
  if (!hasJsonContent(response)) throw new Error('Expected a JSON response.');

  return (await response.json()) as TResponse;
}

export type DownloadedFile = {
  blob: Blob;
  fileName: string;
};

const maxDownloadFileNameLength = 120;
const fallbackDownloadFileName = 'certificate.cer';

export async function toDownloadedFile(response: Response): Promise<DownloadedFile> {
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const fileName = getDownloadFileName(disposition);
  return { blob, fileName };
}

function getDownloadFileName(disposition: string | null): string {
  if (!disposition) return fallbackDownloadFileName;

  const extendedMatch = disposition.match(/(?:^|;)\s*filename\*\s*=\s*(?:UTF-8''|utf-8'')?([^;]+)/i);
  if (extendedMatch?.[1]) return sanitizeFileName(decodeFileName(extendedMatch[1]));

  const standardMatch = disposition.match(/(?:^|;)\s*filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  return sanitizeFileName(standardMatch?.[1] ?? standardMatch?.[2] ?? fallbackDownloadFileName);
}

function decodeFileName(value: string): string {
  const unquoted = value.trim().replace(/^"|"$/g, '');

  try {
    return decodeURIComponent(unquoted);
  } catch {
    return unquoted;
  }
}

function sanitizeFileName(value: string): string {
  const fileName = value
    .split(/[\\/]/)
    .pop()
    ?.split('')
    .map((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || '<>:"/\\|?*'.includes(character) ? '_' : character;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/g, '');

  if (!fileName || fileName === '.' || fileName === '..') return fallbackDownloadFileName;

  const safeFileName = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(?:\..*)?$/i.test(fileName) ? `_${fileName}` : fileName;

  if (safeFileName.length <= maxDownloadFileNameLength) return safeFileName;

  const extensionIndex = safeFileName.lastIndexOf('.');
  if (extensionIndex > 0 && extensionIndex < safeFileName.length - 1) {
    const extension = safeFileName.slice(extensionIndex);
    const baseLength = Math.max(1, maxDownloadFileNameLength - extension.length);
    return `${safeFileName.slice(0, baseLength)}${extension}`;
  }

  return safeFileName.slice(0, maxDownloadFileNameLength);
}

export function toStreamer<TResponse>(response: Response) {
  if (!hasNdJsonContent(response)) throw new Error('Expected an NDJSON response.');

  const body = response.body;
  if (!body) throw new Error('Streaming is not supported on this platform.');

  return Promise.resolve((onNext?: StreamerCallback<TResponse>, signal?: AbortSignal) =>
    stream<TResponse>(body, onNext, signal)
  );
}

async function stream<TResponse>(
  body: ReadableStream<Uint8Array<ArrayBuffer>>,
  onNext: StreamerCallback<TResponse> | undefined,
  signal: AbortSignal | undefined
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) return;

      const { done, value } = await reader.read();
      if (done) {
        if (buffer.trim()) {
          await onNext?.(JSON.parse(buffer));
        }

        return;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.trim()) {
          await onNext?.(JSON.parse(line));
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export function makeGraphRequest<TResponse>(request: (graph: Client) => Promise<TResponse>) {
  const authService = Auth.getInstance();
  const client = authService.getGraphClient();
  return request(client);
}

function hasJsonContent(response: Response): boolean {
  const contentType = response.headers.get('Content-Type');
  if (!contentType) return false;

  return contentType.includes('application/json') || contentType.includes('application/problem+json');
}

function hasNdJsonContent(response: Response): boolean {
  const contentType = response.headers.get('Content-Type');
  if (!contentType) return false;

  return contentType.includes('application/x-ndjson');
}

// Re-exported, not redeclared: makeRequest (@easylife365/react) throws its own HttpError, so a local class of
// the same name would never match an `instanceof` check against what actually gets thrown.
export { HttpError };

/**
 * A refusal the user can act on: the server understood the request and said why it will not carry it out (HTTP 400).
 * Anything else, a network failure or a server error, is not something editing the form fixes.
 */
export function isRefusal(error: unknown): error is HttpError {
  return error instanceof HttpError && error.statusCode === 400;
}
